import { TextInputV2 } from "react-migration/components/TextInputV2";
import { Icon } from "react-migration/components/Icon";
import { useContext, useEffect, useRef, useState } from "react";

import { SiteFiltersContext } from "../SiteFilters";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Button } from "react-migration/components/Button";
import clsx from "clsx";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";

export interface SiteSearchProps {
  className?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const SiteSearch = ({ className, isOpen, setIsOpen }: SiteSearchProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { onFilterUpdate, useSearchString, updateSearchString } = useContext(SiteFiltersContext);

  const searchString = useSearchString();

  const searchRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState(searchString);

  useEffect(() => {
    if (searchString !== value) {
      setValue(searchString);
    }
    // deliberately excluding value here to prevent this effect from running twice
    // because we mutate value as part of the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, onFilterUpdate]);

  const onSubmit = () => {
    updateSearchString(value);
    onFilterUpdate("searchText");
    searchRef.current?.blur();
  };

  return (
    <div data-testid="site-search" className={clsx(className, isMobile && "flex items-center no-wrap gap-2 w-full")}>
      {isMobile && (
        <Button onClick={() => setIsOpen(!isOpen)} variant="ghost">
          <Icon icon={clsx(isOpen ? "icon-lt-close-line" : "icon-lt-search-line")} size="lg" />
        </Button>
      )}
      {(isOpen || !isMobile) && (
        <TextInputV2
          ref={searchRef}
          size={isMobile ? "large" : "small"}
          className={clsx("md:w-full", isMobile && "h-11 z-99 left-12 fixed")}
          dataTestId="site-search-input"
          placeholder={t("sites.pipeline.filters.search_for_sites")}
          IconLeft={
            <Icon icon="icon-lt-search-line" extraClasses={clsx("hidden md:block", isMobile && "block px-2")} />
          }
          iconContainerProps={{
            className: "ml-0 md:ml-3",
          }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onSubmit();
              searchRef.current?.blur();
            } else if (e.key === "Escape") {
              setValue(searchString);
              searchRef.current?.blur();
            }
          }}
        />
      )}
    </div>
  );
};
